import React from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Col, Progress, Row } from "reactstrap";
import styles from "./styles.module.css";
import { useSelector } from "react-redux";
import Tooltip from "react-bootstrap/Tooltip";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import { Icon } from "@iconify/react";
import { getCreditText } from "../../../../helpers/common";
import moment from "moment";
import axios from "axios";
import { useEffect, useState } from "react";

const CreditPanel = () => {
  const navigate = useNavigate();

  // --- 1. Redux Slices ---
  const employerReducer = useSelector((state) => state.employer);
  const isPlanLoading = employerReducer?.isPlanLoading;
  const planDetails = employerReducer?.planDetails;
  const plan = planDetails?.plan;
  const customer = useSelector((state) => state.customer?.profile);

  // --- 2. Local State Pipeline ---
  const [dashboardData, setDashboardData] = useState(null);

  // --- 3. Calculations ---

  const totalCredits = planDetails?.credits || 0;
  const totalDebits = planDetails?.debits || 0;
  const totalBalance = planDetails?.balance || 0;
  const totalJobsAllowed = dashboardData?.maxJobsAllowed || 0;
  const jobsPosted = dashboardData?.jobsPostedInPlan || 0;
  const jobsRemaining = dashboardData?.remainingJobPosts || 0;
  const planRemaining = dashboardData?.daysRemaining || 0;

  const jobPostedPercentage =
    totalJobsAllowed > 0 ? (jobsPosted / totalJobsAllowed) * 100 : 0;
  const creditUsedPercentage =
    totalCredits > 0 ? (totalDebits / totalCredits) * 100 : 0;

  const isCreditFinish = totalBalance <= 0;
  const isPlanExpired = planDetails?.isExpired || false;


  // --- 4. Side Effects ---
  useEffect(() => {
    if (!customer?.id) return;

    const getDashboardData = async () => {
      try {
        const token =
          localStorage.getItem("webvi_authToken") ||
          localStorage.getItem("token");
        const res = await axios.get(
          `https://sdsjobseekers.runasp.net/api/Dashboard/employer?employerId=${customer.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        setDashboardData(res.data?.data || res.data);
      } catch (err) {
        console.error("🛑 Dashboard API Trace Error:", err);
      }
    };

    getDashboardData();
  }, [customer?.id]);

  // --- 5. Render Helper Templates ---
  const renderTooltip = (props, message) => (
    <Tooltip id="button-tooltip" {...props}>
      {message}
    </Tooltip>
  );

  const getExpiryDateText = () => {
    const expiryDateRaw = planDetails?.creditsExpiryDate;
    if (!expiryDateRaw) return null;

    const expiryDate = moment(expiryDateRaw);
    const formattedDate = expiryDate.format("MMMM DD, YYYY");

    const today = moment().startOf("day");
    const expiry = expiryDate.clone().startOf("day");
    const daysLeft = expiry.diff(today, "days") + 1;

    if (isPlanExpired) {
      return (
        <div
          className="alert alert-danger d-inline-flex align-items-center rounded-3 border-0 py-2 px-3 shadow-sm"
          role="alert"
        >
          <Icon
            icon="material-symbols:error-outline"
            className="me-2 text-danger fs-5"
          />
          <span className="small text-danger-emphasis">
            Your credits expired on{" "}
            <span className="fw-bold">{formattedDate}</span>. Please purchase a
            new plan to continue.
          </span>
        </div>
      );
    }

    return (
      <div
        className="alert alert-warning d-inline-flex align-items-center rounded-3 border-0 py-2 px-3 shadow-sm"
        role="alert"
      >
        <Icon
          icon="material-symbols:warning-outline-rounded"
          className="me-2 text-warning fs-5"
        />
        <span className="small text-warning-emphasis fw-medium">
          Credits available until{" "}
          <span className="fw-bold text-decoration-underline">
            {formattedDate}
          </span>
          {daysLeft >= 0 && (
            <span className="ms-1 fw-bold text-danger">
              ({planRemaining} {daysLeft === 1 ? "day" : "days"} left)
            </span>
          )}
        </span>
      </div>
    );
  };

  if (isPlanLoading) return null;

  // --- 6. Fallback: No Active Package View ---
  if (!planDetails?.plan) {
    return (
      <div className="bg-dark text-white rounded-4 p-4 p-lg-5 shadow-sm border border-secondary border-opacity-25">
        <Row className="align-items-center g-4">
          <Col lg={8}>
            <div className="d-flex gap-3 align-items-start">
              <div className="bg-secondary bg-opacity-25 p-3 rounded-3 text-warning">
                <Icon
                  icon="material-symbols:credit-card-outline"
                  fontSize={45}
                />
              </div>
              <div>
                <h3 className="fw-bold tracking-tight mb-2">
                  No Active Package
                </h3>
                <p className="text-secondary mb-3 fs-5 text-light-emphasis">
                  Choose a plan today that fits your hiring needs and start
                  reaching thousands of qualified candidates.
                </p>
                <p className="text-secondary small mb-0 opacity-75">
                  You don't have an active package assigned to your profile.
                  Upgrade your workspace to enjoy premium job postings and
                  unlimited database lookups.
                </p>
              </div>
            </div>
          </Col>
          <Col lg={4} className="text-lg-end text-center">
            <button
              type="button"
              className="btn btn-warning btn-lg fw-semibold px-4 py-2.5 rounded-3 shadow-sm"
              onClick={() => navigate("/pricing")}
            >
              <Icon
                icon="material-symbols:shopping-cart"
                className="me-2 fs-5"
              />
              View Packages & Pricing
            </button>
          </Col>
        </Row>
      </div>
    );
  }

  // --- 7. Main Dashboard Trackers UI View ---
  return (
    <div className="bg-white rounded-4 p-4 shadow-sm border border-light">
      <Row className="g-4">
        {/* Left Side: Summary Card */}
        <Col lg={4}>
          <div className="bg-light rounded-4 p-4 h-100 border border-light d-flex flex-column justify-content-between">
            <div>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <span className="text-muted small fw-semibold text-uppercase tracking-wider">
                  Current Package
                </span>
                <Badge
                  color={isPlanExpired ? "danger" : "success"}
                  className="px-2.5 py-1.5 rounded-pill shadow-xs"
                >
                  {isPlanExpired ? "Expired" : "Active"}
                </Badge>
              </div>

              <div className="d-flex align-items-center gap-2 mb-4">
                <h4 className="fw-bold text-dark mb-0">
                  {plan?.name || "Premium Plan"}
                </h4>
                {plan?.description && (
                  <OverlayTrigger
                    placement="right"
                    delay={{ show: 250, hide: 400 }}
                    overlay={(e) => renderTooltip(e, plan?.description)}
                  >
                    <span
                      className="text-muted link-secondary"
                      style={{ cursor: "pointer" }}
                    >
                      <Icon
                        icon="material-symbols:info-outline"
                        fontSize={18}
                      />
                    </span>
                  </OverlayTrigger>
                )}
              </div>
            </div>

            {/* HORIZONTAL COUNTERS SECTION */}
            <div className="mt-auto pt-3 border-top border-light-subtle">
              <Row className="g-0 align-items-center">
                {/* Left Side: Wallet Credits */}
                <Col xs={6} className="pe-2">
                  <span
                    className="text-muted d-block text-truncate mb-1"
                    style={{ fontSize: "12px", fontWeight: "500" }}
                  >
                    Remaining Credits
                  </span>
                  <div className="d-flex align-items-baseline gap-1 flex-wrap">
                    <h2 className="fw-bold text-dark mb-0">{totalBalance}</h2>
                    <span className="text-secondary small">
                      / {totalCredits}
                    </span>
                  </div>
                </Col>

                {/* Right Side: Job Slots */}
                <Col xs={6} className="ps-3 border-start border-light-subtle">
                  <span
                    className="text-muted d-block text-truncate mb-1"
                    style={{ fontSize: "12px", fontWeight: "500" }}
                  >
                    Remaining Jobs
                  </span>
                  <div className="d-flex align-items-baseline gap-1 flex-wrap">
                    <h2 className="fw-bold text-dark mb-0">{jobsRemaining}</h2>
                    <span className="text-secondary small">
                      / {totalJobsAllowed}
                    </span>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </Col>

        {/* Right Side: Usage Trackers */}
        <Col lg={8} className="ps-lg-4">
          <div className="d-flex flex-column h-100 justify-content-between">
            {/* PROGRESS METRIC 1: Jobs Posted allocation summary */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-end mb-2">
                <div>
                  <span className="text-muted d-block small mb-1">
                    Job Postings Usage
                  </span>
                  <div className="d-flex align-items-baseline gap-1">
                    <h5 className="fw-bold text-dark mb-0">{jobsPosted}</h5>
                    <span className="text-muted small">
                      / {totalJobsAllowed} Used
                    </span>
                  </div>
                </div>
                <div className="text-end">
                  <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1.5 rounded-3 fw-semibold">
                    {jobsRemaining} Remaining Slots
                  </span>
                </div>
              </div>
              <Progress
                value={jobPostedPercentage}
                color={jobsRemaining <= 0 ? "danger" : "success"}
                className="rounded-pill overflow-hidden bg-light border"
                style={{ height: "10px" }}
              />
              <div className="d-flex justify-content-between mt-1 px-1">
                <small className="text-muted">
                  Total allowable post volume limit
                </small>
                <small className="fw-bold text-dark">
                  {Math.round(jobPostedPercentage)}%
                </small>
              </div>
            </div>

            {/* PROGRESS METRIC 2: Core Credit Consumption Summary */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-end mb-2">
                <div>
                  <span className="text-muted d-block small mb-1">
                    Credits Consumption Rate
                  </span>
                  <div className="d-flex align-items-baseline gap-1">
                    <h5 className="fw-bold text-dark mb-0">{totalDebits}</h5>
                    <span className="text-muted small">Debited</span>
                  </div>
                </div>
                <div className="text-end">
                  <span
                    className={`badge ${
                      isCreditFinish
                        ? "bg-danger-subtle text-danger"
                        : "bg-primary-subtle text-primary"
                    } border px-2 py-1.5 rounded-3 fw-semibold`}
                  >
                    Wallet Balance: {totalBalance} Cr
                  </span>
                </div>
              </div>
              <Progress
                value={creditUsedPercentage}
                color={
                  isCreditFinish
                    ? "danger"
                    : isPlanExpired
                    ? "warning"
                    : "primary"
                }
                className="rounded-pill overflow-hidden bg-light border"
                style={{ height: "10px" }}
              />
              <div className="d-flex justify-content-between mt-1 px-1">
                <small className="text-muted">
                  Account balance lifecycle pool consumption
                </small>
                <small className="fw-bold text-dark">
                  {Math.round(creditUsedPercentage)}%
                </small>
              </div>
            </div>

            {/* Bottom Section: Actions and Alert */}
            <div className="border-top border-light-subtle pt-3 mt-auto">
              <div className="d-flex flex-wrap gap-3 justify-content-between align-items-center">
                <div>{getExpiryDateText()}</div>

                <div className="d-flex gap-2">
                  {isPlanExpired && (
                    <button
                      type="button"
                      className="btn btn-danger font-medium rounded-3 px-4 shadow-sm py-2"
                      onClick={() => navigate("/pricing")}
                    >
                      Purchase Plan
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-outline-dark font-medium rounded-3 px-4 py-2"
                    onClick={() => navigate("/employer/orders")}
                  >
                    View Ledger Statement
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default CreditPanel;
